import React, { useMemo } from 'react';
import SimulationCard from './SimulationCard';
import SimulationStack from './SimulationStack';
import EmptyState from './EmptyState';
import { OmbudsmanSimulation } from '../../../../types/workspace';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Scale } from 'lucide-react';

interface SimulationsGridProps {
  simulations: OmbudsmanSimulation[];
  onOpen?: (id: string) => void;
  onExportPDF?: (id: string) => void;
  onDownloadJSON?: (id: string) => void;
  onArchive?: (id: string) => void;
  onStartNew?: () => void;
  onClearFilters?: () => void;
  isFiltered: boolean;
}

const SimulationsGrid: React.FC<SimulationsGridProps> = ({
  simulations,
  onOpen,
  onExportPDF,
  onDownloadJSON,
  onArchive,
  onStartNew,
  onClearFilters,
  isFiltered,
}) => {
  // Group simulations by signal_id
  const groupedSimulations = useMemo(() => {
    const groups = new Map<string, OmbudsmanSimulation[]>();

    simulations.forEach((sim) => {
      const signalId = sim.signal?.signal_id || sim.id;
      if (!groups.has(signalId)) {
        groups.set(signalId, []);
      }
      groups.get(signalId)!.push(sim);
    });

    // Sort each group by updated_at (most recent first)
    groups.forEach((sims) => {
      sims.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
    });

    return Array.from(groups.values());
  }, [simulations]);

  // Show empty state
  if (simulations.length === 0) {
    if (isFiltered) {
      // No search results
      return (
        <EmptyState
          icon={<MagnifyingGlassIcon className="w-12 h-12 text-muted-foreground" />}
          title="No cases match your search"
          description="Try adjusting your filters or search terms to find what you're looking for."
          secondaryAction={
            onClearFilters
              ? {
                  label: 'Clear all filters',
                  onClick: onClearFilters,
                }
              : undefined
          }
        />
      );
    }

    // No cases at all
    return (
      <EmptyState
        icon={<Scale className="w-12 h-12 text-muted-foreground" />}
        title="No cases yet"
        description="Start your first case guidance to analyze how the FOS might reason about your cases."
        primaryAction={
          onStartNew
            ? {
                label: 'Start New Case',
                onClick: onStartNew,
              }
            : undefined
        }
      />
    );
  }

  // Show grid of simulations (stacked or individual)
  return (
    <div className="px-6 py-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {groupedSimulations.map((group) => {
          if (group.length > 1) {
            // Multiple simulations for same signal - show as stack
            return (
              <SimulationStack
                key={group[0].signal?.signal_id || group[0].id}
                simulations={group}
                onOpen={onOpen}
                onExportPDF={onExportPDF}
                onDownloadJSON={onDownloadJSON}
                onArchive={onArchive}
              />
            );
          } else {
            // Single simulation - show as regular card
            return (
              <SimulationCard
                key={group[0].id}
                simulation={group[0]}
                onOpen={onOpen}
                onExportPDF={onExportPDF}
                onDownloadJSON={onDownloadJSON}
                onArchive={onArchive}
              />
            );
          }
        })}
      </div>
    </div>
  );
};

export default SimulationsGrid;
